import { useState, useEffect } from 'react';
import { MapPin, Search, Calendar, ChevronDown, Plus, Map, List, Navigation2, Filter } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import TandemLogo from '@/assets/tandem-logo.svg';
import PenguinLogo from '@/assets/penguin-logo.png';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// L1 Category emoji mapping
const CATEGORY_EMOJIS = {
  'sports': '⚽',
  'music': '🎵',
  'art': '🎨',
  'food': '🍔',
  'social': '🤝',
  'gaming': '🎮',
  'screenings': '🎬',
  'parties': '🎉'
};

export default function Header({ searchQuery, onSearchChange, dateFilter, onDateFilterChange, allEvents, onSearchSubmit, onListEvent, viewMode, onViewModeChange, onLocationSearch, categoryFilter, onCategoryFilterChange }) {
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showLocationSearch, setShowLocationSearch] = useState(false);
  const [locationQuery, setLocationQuery] = useState('');
  const [showCategoryFilter, setShowCategoryFilter] = useState(false);
  const [categories, setCategories] = useState([]);

  // Sync selectedDate with dateFilter when dateFilter changes externally
  useEffect(() => {
    if (dateFilter === 'today' || dateFilter === 'tomorrow' || dateFilter === 'all') {
      setSelectedDate(null);
    } else if (dateFilter && dateFilter.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // dateFilter is a date string, parse it
      const [year, month, day] = dateFilter.split('-').map(Number);
      setSelectedDate(new Date(year, month - 1, day));
    }
  }, [dateFilter]);

  // Fetch categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/categories`);
        if (response.ok) {
          const data = await response.json();
          setCategories(data);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      onSearchSubmit();
    }
  };

  const getDateFilterLabel = () => {
    if (dateFilter === 'all') return 'All Events';
    if (dateFilter === 'today') return 'Today';
    if (dateFilter === 'tomorrow') return 'Tomorrow';
    if (selectedDate) return selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return 'All Events';
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    // Format as YYYY-MM-DD for proper date comparison
    // Use local date components to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    console.log('📅 Date selected:', { date: date.toString(), formatted: formattedDate });
    onDateFilterChange(formattedDate);
    setShowDateFilter(false);
  };

  const popularLocations = [
    { name: 'Bengaluru', lat: 12.9716, lng: 77.6412 },
    { name: 'Delhi', lat: 28.6139, lng: 77.2090 },
    { name: 'Gurgaon', lat: 28.4595, lng: 77.0266 },
    { name: 'Mumbai', lat: 19.0760, lng: 72.8777 },
    { name: 'Hyderabad', lat: 17.3850, lng: 78.4867 },
    { name: 'Pune', lat: 18.5204, lng: 73.8567 },
    { name: 'Chennai', lat: 13.0827, lng: 80.2707 },
    { name: 'Kolkata', lat: 22.5726, lng: 88.3639 },
  ];

  const handleLocationSelect = (location) => {
    if (onLocationSearch) {
      onLocationSearch(location);
    }
    setShowLocationSearch(false);
    setLocationQuery('');
  };

  const filteredLocations = locationQuery 
    ? popularLocations.filter(loc => 
        loc.name.toLowerCase().includes(locationQuery.toLowerCase())
      )
    : popularLocations;

  return (
    <header className="fixed top-0 left-0 right-0 bg-card border-b border-border shadow-md z-50">
      <div className="max-w-full mx-auto pr-0.5 sm:px-4 lg:px-8">
        <div className="flex items-center justify-between h-12 sm:h-16 gap-2 sm:gap-4">
          {/* Logo - Penguin on mobile/iPad, Tandem on desktop */}
          <div className="flex items-center flex-shrink-0">
            {/* Penguin logo for mobile/iPad - transparent background */}
            <img 
              src={PenguinLogo} 
              alt="Logo" 
              className="h-8 sm:h-10 w-auto lg:hidden"
              style={{ objectFit: 'contain' }}
            />
            {/* Tandem logo for desktop */}
            <img 
              src={TandemLogo} 
              alt="Tandem Logo" 
              className="hidden lg:block h-12 w-auto"
            />
          </div>

          {/* Search Bar - Adjusted width for mobile/iPad with logo */}
          <div className="flex-1 flex gap-0.5 sm:gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground z-10" />
              <Input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                className="pl-8 sm:pl-10 pr-2 text-base sm:text-sm h-9 sm:h-10 bg-muted/50 border-border focus:ring-primary"
              />
            </div>
            
            {/* Location Search - Now visible on mobile */}
            <Popover open={showLocationSearch} onOpenChange={setShowLocationSearch}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="flex items-center space-x-0.5 sm:space-x-2 whitespace-nowrap h-9 sm:h-10 px-1.5 sm:px-4">
                  <Navigation2 className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="text-xs sm:text-sm hidden sm:inline">Location</span>
                  <ChevronDown className="h-2.5 w-2.5 sm:h-3.5 sm:w-3.5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-2" align="end">
                <div className="space-y-2">
                  <Input
                    type="text"
                    placeholder="Search locations..."
                    value={locationQuery}
                    onChange={(e) => setLocationQuery(e.target.value)}
                    className="mb-2"
                  />
                  <div className="max-h-64 overflow-y-auto space-y-1">
                    {filteredLocations.map((location) => (
                      <button
                        key={location.name}
                        onClick={() => handleLocationSelect(location)}
                        className="w-full text-left px-3 py-2 text-sm rounded hover:bg-muted transition-smooth flex items-center space-x-2"
                      >
                        <MapPin className="h-4 w-4 text-primary" />
                        <span>{location.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Right Section - Category Filter + View Toggle + List Event Button + Date Filter */}
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            {/* Category Filter */}
            <Popover open={showCategoryFilter} onOpenChange={setShowCategoryFilter}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="flex items-center space-x-0.5 sm:space-x-2 whitespace-nowrap h-9 sm:h-10 px-1.5 sm:px-4">
                  {categoryFilter ? (
                    <span className="text-xs sm:text-base">{CATEGORY_EMOJIS[categoryFilter.l1] || '📌'}</span>
                  ) : (
                    <Filter className="h-3 w-3 sm:h-4 sm:w-4" />
                  )}
                  <span className="text-xs sm:text-sm hidden sm:inline">
                    {categoryFilter ? 
                      (categories.find(c => c._id === categoryFilter.l1)?.name || 'Category') :
                      'All Categories'}
                  </span>
                  <ChevronDown className="h-2.5 w-2.5 sm:h-3.5 sm:w-3.5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-2" align="end">
                <div className="max-h-96 overflow-y-auto space-y-1">
                  {/* All Categories Option */}
                  <button
                    onClick={() => {
                      onCategoryFilterChange(null);
                      setShowCategoryFilter(false);
                    }}
                    className={`w-full text-left px-4 py-3 text-sm rounded-lg font-medium transition-smooth flex items-center space-x-3 ${
                      !categoryFilter ? 'bg-primary text-primary-foreground shadow-sm' : 'hover:bg-muted/60'
                    }`}
                  >
                    <span className="text-lg">🌟</span>
                    <span>All Categories</span>
                  </button>
                  
                  {/* L1 Categories with Emojis - Pretty Design */}
                  {categories.map((category) => {
                    const emoji = CATEGORY_EMOJIS[category._id] || '📌';
                    return (
                      <button
                        key={category._id}
                        onClick={() => {
                          onCategoryFilterChange({ l1: category._id, l2: null });
                          setShowCategoryFilter(false);
                        }}
                        className={`w-full text-left px-4 py-3 text-sm rounded-lg transition-smooth flex items-center space-x-3 ${
                          categoryFilter?.l1 === category._id 
                            ? 'bg-primary/15 text-primary font-semibold border border-primary/30 shadow-sm' 
                            : 'hover:bg-muted/60 hover:shadow-sm'
                        }`}
                      >
                        <span className="text-lg">{emoji}</span>
                        <span>{category.name}</span>
                      </button>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>

            {/* View Mode Toggle - Hidden on mobile and iPad (shown in bottom nav), only on desktop */}
            <div className="hidden lg:flex items-center bg-muted rounded-lg p-0.5 sm:p-1">
              <Button
                onClick={() => onViewModeChange('map')}
                variant={viewMode === 'map' ? 'default' : 'ghost'}
                size="sm"
                className={`h-7 sm:h-8 px-1.5 sm:px-2 md:px-3 ${viewMode === 'map' ? 'shadow-sm' : ''}`}
              >
                <Map className="h-3.5 w-3.5 sm:h-4 sm:w-4 md:mr-1" />
                <span className="text-xs font-medium hidden md:inline">Map</span>
              </Button>
              <Button
                onClick={() => onViewModeChange('list')}
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                className={`h-7 sm:h-8 px-1.5 sm:px-2 md:px-3 ${viewMode === 'list' ? 'shadow-sm' : ''}`}
              >
                <List className="h-3.5 w-3.5 sm:h-4 sm:w-4 md:mr-1" />
                <span className="text-xs font-medium hidden md:inline">List</span>
              </Button>
            </div>

            <Button
              onClick={onListEvent}
              variant="outline"
              className="hidden lg:flex items-center space-x-2 border-primary text-primary hover:bg-primary/10"
            >
              <Plus className="h-4 w-4" />
              <span className="text-sm font-medium">List Your Event</span>
            </Button>

            {/* Date Filter */}
            <Popover open={showDateFilter} onOpenChange={setShowDateFilter}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="flex items-center space-x-0.5 sm:space-x-2 whitespace-nowrap h-9 sm:h-10 px-1.5 sm:px-4">
                  <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="text-xs sm:text-sm hidden sm:inline">
                    {dateFilter === 'all' ? 'All Events' :
                     dateFilter === 'today' ? 'Today' :
                     dateFilter === 'tomorrow' ? 'Tomorrow' :
                     selectedDate ? selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Date'}
                  </span>
                  <ChevronDown className="h-2.5 w-2.5 sm:h-3.5 sm:w-3.5 hidden sm:block" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 max-w-[calc(100vw-2rem)]" align="end" sideOffset={5}>
                <div className="flex flex-col">
                  <div className="p-2 border-b border-border">
                    <button
                      onClick={() => {
                        onDateFilterChange('today');
                        setSelectedDate(null);
                        setShowDateFilter(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm rounded transition-smooth ${
                        dateFilter === 'today' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                      }`}
                    >
                      Today
                    </button>
                    <button
                      onClick={() => {
                        onDateFilterChange('tomorrow');
                        setSelectedDate(null);
                        setShowDateFilter(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm rounded transition-smooth ${
                        dateFilter === 'tomorrow' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                      }`}
                    >
                      Tomorrow
                    </button>
                    <button
                      onClick={() => {
                        onDateFilterChange('all');
                        setSelectedDate(null);
                        setShowDateFilter(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm rounded transition-smooth ${
                        dateFilter === 'all' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                      }`}
                    >
                      All Events
                    </button>
                  </div>
                  <div className="p-3">
                    <p className="text-xs text-muted-foreground mb-2 font-medium">Custom Date</p>
                    <CalendarComponent
                      mode="single"
                      selected={selectedDate}
                      onSelect={handleDateSelect}
                      disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                      className="rounded-md border"
                    />
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>
    </header>
  );
}
